import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Typography, Box, Modal, Button, TextField } from '@mui/material';
import { getModalBoxStyle } from '../../../../utils/commonFunc';

const BookEditInvoice = ({ 
  selectedRow, 
  selectedRowInvoiceNumber, 
  onBookingSuccess, 
  onClose,
  autoShow = false,
  selectedInvoices = [], // Add this to receive the full invoice objects
  selectedTabValue
}) => {
  const [bookInvoiceModal, setBookInvoiceModal] = useState({
    open: false,
    invoiceNumber: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (autoShow && selectedRowInvoiceNumber) {
      setBookInvoiceModal({
        open: true,
        invoiceNumber: selectedRowInvoiceNumber,
      });
    }
  }, [autoShow, selectedRowInvoiceNumber]);

  const handleButtonClick = async () => {
    const invoiceNumber = selectedRowInvoiceNumber || selectedRow?.invoiceNumber;
    
    if (!invoiceNumber) {
      toast.error('Invoice number not found');
      return;
    }

    if (!selectedRow?.isBooked) {
      // Show booking confirmation modal for unbooked invoices only
      setBookInvoiceModal({
        open: true,
        invoiceNumber: invoiceNumber,
      });
    }
    // Do nothing if invoice is already booked
  };

  const handleConfirmBookInvoice = async () => {
    try {
      setLoading(true);
      
      const invoiceNumber = bookInvoiceModal.invoiceNumber;
      
      if (!invoiceNumber) {
        toast.error('Invoice number not found');
        return;
      }

      const invoiceNumbersArray = Array.isArray(invoiceNumber) ? invoiceNumber : [invoiceNumber];
      
      const response = await axios.put(`/admin/invoice/update-booking-status`, {
        InvoiceNumbers: invoiceNumbersArray
      });
      if (response.status === 206) {
        let message = response.data|| 'Failed to book invoice';
        const alreadyBookedLine = message
          .split("\n")
          .find(line => line.includes("Already booked"));
     
        if (alreadyBookedLine) {
          toast.warn(alreadyBookedLine);
     
          message = message
            .split("\n")
            .filter(line => !line.includes("Already booked"))
            .join("\n");
        }
     
        if (message.trim()) {
          toast.error(message);
        }
        setBookInvoiceModal({ open: false, invoiceNumber: null });
      }
     if (response.status === 200) {
        let message = response.data.message || 'Invoice booked successfully';
        const alreadyBookedLine = message
          .split("\n")
          .find(line => line.includes("Already booked"));
     
        if (alreadyBookedLine) {
          toast.warn(alreadyBookedLine);
     
          message = message
            .split("\n")
            .filter(line => !line.includes("Already booked"))
            .join("\n");
        }
     
        if (message.trim()) {
          toast.success(message);
        }
    
        setBookInvoiceModal({ open: false, invoiceNumber: null });
        // Notify parent component of successful booking
        if (onBookingSuccess) {
          onBookingSuccess();
        }
      }
    } catch (err) {
      console.error('Book invoice error:', err);
      toast.error(err?.response?.data?.message || 'Failed to book invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBookInvoice = () => {
    setBookInvoiceModal({
      open: false,
      invoiceNumber: null,
    });
    if (onClose) {
      onClose();
    }
  };

  const isAllInvoicesBooked = () => {
    // When used in toolbar with multiple selected invoices
    if (autoShow && selectedInvoices.length > 0) {
      return selectedInvoices.every(invoice => invoice.isBooked);
    }
    
    // Check if dealing with multiple invoices (array of invoice numbers)
    if (Array.isArray(selectedRowInvoiceNumber)) {
      // For multiple invoices, check if all corresponding invoices are booked
      if (selectedInvoices.length > 0) {
        return selectedInvoices.every(invoice => invoice.isBooked);
      }
      // Fallback: if we don't have invoice objects, assume not all are booked
      return false;
    }
    
    // Single invoice case
    return selectedRow?.isBooked || false;
  };

  return (
    <>
      {/* Book Invoice Button - Only show if not autoShow */}
      {selectedRow?.isHaveCreditInvoice && (
        <Box sx={{ my: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#e3f2fd', borderRadius: 6, padding: '8px 16px', boxShadow: '0 2px 8px rgba(33,150,243,0.08)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8 }}>
              <circle cx="12" cy="12" r="12" fill="#1976d2"/>
              <path d="M12 7v5l3 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2', margin: 0 }}>
              A credit invoice has been created : <span style={{ fontWeight: 700 }}>{selectedRow?.creditInvoiceNumber}</span>
            </Typography>
          </div>
        </Box>
      )}
      {!autoShow && (
        <Button
          variant="outlined"
          disabled={loading || isAllInvoicesBooked() || selectedTabValue == 6 || selectedRow?.invoicePurposeType === 3}
          onClick={handleButtonClick}
          style={{ opacity: selectedTabValue == 6 ? 0.5 : 1 }}
        >
          {isAllInvoicesBooked() ? 'Already Booked' : 'Book Invoice'}
        </Button>
      )}

      {/* Book Invoice Confirmation Modal */}
      <Modal
        open={bookInvoiceModal.open}
        onClose={handleCancelBookInvoice}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={getModalBoxStyle()}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to book {Array.isArray(bookInvoiceModal.invoiceNumber) ? bookInvoiceModal.invoiceNumber.length : '1'} invoice{Array.isArray(bookInvoiceModal.invoiceNumber) && bookInvoiceModal.invoiceNumber.length > 1 ? 's' : ''}?
          </Typography>
          <div
            style={{ margin: '25px 0 0', float: 'right', gap: '10px', display: 'flex', alignItems: 'center' }}
          >
            <Button
              onClick={handleCancelBookInvoice}
              sx={{ float: 'right', backgroundColor: '#62b5e514' }}
              autoFocus
            >
              No
            </Button>
            <Button
              onClick={handleConfirmBookInvoice}
              sx={{ float: 'right', backgroundColor: '#62b5e514' }}
              autoFocus
              disabled={loading}
            >
              {loading ? 'Booking...' : 'Yes'}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default BookEditInvoice;